import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject} from '@angular/core';
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogService} from "../../../shared/services/dialog.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {CalendarEventResource} from '../../../shared/types/calendar-types/calendarEventResource';
import {ApiService} from "../../../shared/services/api.service";
import {Subscription, debounceTime, distinctUntilChanged} from "rxjs";
import {AvatarComponent} from "../../../shared/elements/avatar/avatar.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {UserMinimalResource} from "../../../shared/types/knowledgebase-types/userMinimalResource";

@Component({
    selector: 'app-dialog-calendar-participants',
    imports: [
        DialogModule,
        FormInputComponent,
        HeadlineComponent,
        TooltipComponent,
        TranslatePipe,
        AvatarComponent,
        ContentComponent
    ],
    template: `
  <ejs-dialog
    target=".dialogs" 
    class="simple-dialog" 
    [visible]='visible' 
    [animationSettings]='dialogService.getAnimationSettings()' 
    [resizeHandles]='dialogService.getResizeHandleDirection()' 
    [position]='dialogService.getPosition()' 
    [isModal]="true" 
    [allowDragging]="false" 
    [width]="'40%'" 
    [height]="'60vh'" 
    [zIndex]="999" 
    [showCloseIcon]="true" 
    (close)="visibleChange.emit(false)"
  >
    <ng-template #header>
      <div class="d-flex align-items-center justify-content-center">
        <app-headline [title]="('CALPARTICIPANTS' | translate)" [type]="'h5'" [class]="'text-primary'">
        </app-headline>
        <app-tooltip 
          [icon]="'help'" 
          [iconClass]="'fs-5 p-1'" 
          [text]="('CALPARTICIPANTSHELP' | translate)" 
          [width]="'300px'" 
          [height]="'100px'" 
          [cssClass]="'helpTooltip p-2'" 
          [opensOn]="'Click'">
        </app-tooltip>
      </div>
    </ng-template>
    <ng-template #content>
      <div class="d-flex flex-column h-100 p-3">
        <app-form-input 
          [type]="'search'" 
          [placeholder]="('SEARCHFORPARTICIPANT' | translate)" 
          [iconFront]="'search'" 
          [iconBack]="'close'" 
          [iconClickType]="'back'" 
          [fCN]="'searchString'" 
          [fG]="calendarParticipantsForm">
        </app-form-input>

        <!-- Scrollable content-->
        <div class="custom-scrollbar overflow-y-auto h-100">
          @if(participants.length === 0) {
            <app-headline [title]="('CALNOPARTICIPANTSFOUND' | translate)" [type]="'h6'" [class]="'text-center text-secondary mt-3'">
            </app-headline>
          } @else {
            @for (participant of participants; track participant.id) {
              <div class="card selected-user-group mb-2">
                <div class="d-flex align-items-center align-items-stretch">
                  <div class="d-flex align-items-center p-1 me-1">
                    <app-avatar
                      [image]="participant.avatar?.path ?? ''"
                      [name]="participant.last_name + ', ' + participant.first_name"
                      [type]="'small'"
                      [icon]="'person'"
                      [border]="false">
                    </app-avatar>
                  </div>
                  <div class="flex-grow-1 lh-1 d-flex align-items-center">
                    <app-tooltip [text]="participant.last_name + ', ' + participant.first_name" [opensOn]="'Auto'">
                      <app-headline
                        [type]="'h6'"
                        [title]="participant.last_name + ', ' + participant.first_name"
                        [class]="'text-muted fs-small'">
                      </app-headline>
                      <app-content [fontSize]="'fs-verysmall'" [fontColor]="'text-light'">{{ 'USER' | translate }}</app-content>
                    </app-tooltip>
                  </div>
                </div>
              </div>
            }
          }
        </div>
      </div>
    </ng-template>
  </ejs-dialog>
  `,
  styles: [`
    .selected-user-group {
      min-height: 40px;
    }
  `]
})
export class DialogCalendarParticipantsComponent implements OnInit, OnChanges, OnDestroy {
  public dialogService: DialogService = inject(DialogService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private subscriptions: Subscription[] = [];
  private initialized: boolean = false;

  @Input() public calenderEvent: CalendarEventResource = {} as CalendarEventResource;
  @Input() public visible: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public participants: UserMinimalResource[] = [];

  public calendarParticipantsForm: FormGroup = this.formBuilder.group({
    searchString: [''],
  });

  public ngOnInit(): void {
    this.initialized = true;
    this.subscriptions.push(
      this.calendarParticipantsForm.controls['searchString'].valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe((value: string | null) => {
          this.loadParticipants((value ?? '').trim());
        })
    );

    this.loadParticipants();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && (changes['calenderEvent'] || changes['visible'])) {
      this.loadParticipants((this.calendarParticipantsForm.controls['searchString'].value ?? '').trim());
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private loadParticipants(search: string = ''): void {
    const eventId = this.calenderEvent?.id;
    if (!this.visible || eventId === undefined || eventId === null || eventId < 1) {
      this.participants = [];
      return;
    }

    this.subscriptions.push(
      this.apiService.getCalendarEventParticipants(eventId, {search: search || undefined})
        .subscribe({
          next: (users) => {
            this.participants = users;
          },
          error: () => {
            this.participants = [];
          }
        })
    );
  }
}
